import { FC } from 'react';
import { createPortal } from 'react-dom';
import DotsThreeCircle from '@components/Icons/DotsThreeCircle';
import { Icon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { m } from 'framer-motion';
import usePopover from '@/hooks/usePopover';

import s from './SecondaryActions.module.scss';

type Props = {
  ariaLabel: string;
  options: Array<{
    value: string;
    icon?: Icon;
    disabled?: boolean;
    destructive?: boolean;
  }>;
  onSelect: (value: string) => void | Promise<void>;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  customIcon?: Icon;
  className?: string;
  compact?: boolean;
};

const SecondaryActions: FC<Props> = ({
  ariaLabel,
  options,
  onSelect,
  position = 'bottom-right',
  customIcon,
  className,
  compact,
}) => {
  const {
    isOpen,
    setIsOpen,
    menuRef,
    triggerRef,
  } = usePopover(position);

  const IconComponent = customIcon || DotsThreeCircle;

  if (options.length === 0) return null;

  return (
    <>
      <div
        className={classNames(
          s.container,
          compact && s.containerCompact,
          className,
        )}
      >
        <button
          type="button"
          ref={triggerRef}
          aria-label={ariaLabel}
          className={s.trigger}
          onClick={() => setIsOpen(!isOpen)}
        >
          <IconComponent
            weight={isOpen ? 'fill' : 'regular'}
            size={compact ? 20 : 24}
          />
        </button>
      </div>
      {isOpen
        && createPortal(
          <m.div
            ref={menuRef}
            className={classNames(s.menu, compact && s.menuCompact)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ul>
              {options.map((option) => (
                <li key={option.value}>
                  <button
                    className={s.menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(option.value);
                      setIsOpen(false);
                    }}
                    disabled={option.disabled}
                    style={{
                      color: option.destructive
                        ? 'var(--color-error)'
                        : 'var(--color-gray-100)',
                    }}
                  >
                    {option.icon && <option.icon size={16} weight="fill" />}
                    {option.value}
                  </button>
                </li>
              ))}
            </ul>
          </m.div>,
          document.body,
        )}
    </>
  );
};

export default SecondaryActions;
